// Ponte entre os elencos REAIS e o gerador procedural.
//
// A regra: o atleta real entra primeiro, na posicao e no nivel do catalogo; o
// que faltar para fechar o elenco e gerado. Assim o catalogo pode estar
// incompleto (o do NBB esta, de proposito) sem que nenhum clube entre em quadra
// com menos gente do que precisa.
//
// Os atributos individuais do atleta real sao derivados do overall dele com o
// mesmo perfil posicional do gerador -- entao um pivo real tem toco e rebote
// altos e arremesso de 3 baixo, como qualquer pivo do jogo.

import { NBA_ROSTERS } from './rosters-nba.js';
import { NBB_ROSTERS } from './rosters-nbb.js';
import { makePlayer, assignTargetMinutes, POSITIONS } from '../engine/players.js';
import { makeRng, clamp } from '../engine/rng.js';

const CATALOGS = { nba: NBA_ROSTERS, nbb: NBB_ROSTERS };

/**
 * PERFIS: o sexto item da linha do catalogo, opcional.
 *
 * Existe porque derivar todos os atributos do overall faz um atleta de 97 ser
 * 97 em TUDO -- e ai Giannis vira rei do arremesso de 3, o que qualquer um que
 * assiste basquete percebe na hora. O perfil corrige o que a posicao sozinha
 * nao conta: quem nao arremessa de fora, quem so faz isso, quem vive da defesa.
 */
const PROFILES = {
  // Domina por dentro e quase nao arremessa de 3.
  sem3: { threeShot: -26, threeTend: -34, twoShot: 6 },
  // Especialista de fora.
  atirador: { threeShot: 9, threeTend: 22, rebOff: -6 },
  // Vive da defesa; termina menos jogadas.
  defensor: { defense: 11, steal: 8, block: 7, usage: -12, threeTend: -4 },
  // Cria para os outros.
  armador: { passing: 13, handle: 9, usage: 4 },
  // Garrafao classico: rebote e toco, sem arremesso.
  paredao: { rebOff: 10, rebDef: 10, block: 12, threeShot: -30, threeTend: -40, freeThrow: -12 },
};

/** Quantos atletas reais o catalogo tem para este clube. */
export function realCountFor(leagueId, teamName) {
  return (CATALOGS[leagueId]?.[teamName] || []).length;
}

/** Cobertura do catalogo de uma liga, para a documentacao e os testes. */
export function catalogCoverage(leagueId) {
  const catalog = CATALOGS[leagueId] || {};
  const clubs = Object.keys(catalog).length;
  const players = Object.values(catalog).reduce((a, list) => a + list.length, 0);
  return { clubs, players };
}

/**
 * Monta o elenco de um clube: reais primeiro, ficticios completando.
 *
 * @param {object} o
 * @param {string} o.leagueId
 * @param {string} o.teamName  nome exato como esta no catalogo de ligas
 * @param {number} o.size      tamanho final do elenco
 */
export function buildRoster({
  leagueId, teamName, size, season = 1, strength = 0, level = 75, spread = 1,
  nationality = 'BR', takenNames = new Set(), fallback,
}) {
  const real = CATALOGS[leagueId]?.[teamName] || [];
  const roster = [];

  for (const [name, position, overall, potential, age, profile] of real) {
    if (roster.length >= size) break;
    if (takenNames.has(name)) continue; // atleta em dois clubes: o primeiro fica
    takenNames.add(name);

    // O talento que reproduz aquele overall na fabrica de atletas do jogo.
    const player = makePlayer({
      position: POSITIONS.includes(position) ? position : 'ALA',
      talent: overall - 75,
      nationality: leagueId === 'nbb' ? 'BR' : 'US',
      age,
      seed: `real|${leagueId}|${teamName}|${name}`,
      takenNames: new Set(), // o nome real ja esta reservado acima
    });
    player.name = name;
    player.overall = overall;
    player.potential = Math.max(overall, potential);
    player.real = true;

    // O perfil ajusta os atributos SEM mexer no overall: o atleta continua tao
    // bom quanto e, so deixa de ser bom naquilo que ele nao faz.
    const ajuste = PROFILES[profile];
    if (ajuste) {
      for (const [attr, delta] of Object.entries(ajuste)) {
        player.attributes[attr] = Math.round(clamp((player.attributes[attr] ?? 75) + delta, 25, 99));
      }
      player.profile = profile;
    }
    roster.push(player);
  }

  // Completa com ficticios, respeitando as posicoes que ficaram descobertas.
  if (roster.length < size) {
    const need = size - roster.length;
    const filler = fallback({
      teamId: `${leagueId}|${teamName}`,
      season, strength, level, spread, nationality, takenNames,
      size: need,
    });
    // Os ficticios entram um degrau abaixo do pior real, para o catalogo nao
    // ser atropelado por um gerado melhor que o astro do clube.
    const worstReal = roster.length ? Math.min(...roster.map((p) => p.overall)) : 99;
    const rng = makeRng(`filler|${leagueId}|${teamName}|${season}`);
    for (const p of filler) {
      if (roster.length && p.overall > worstReal) {
        p.overall = Math.round(clamp(worstReal - 1 - rng.int(4), 40, 99));
        p.potential = Math.max(p.overall, p.potential);
      }
      roster.push(p);
    }
  }

  assignTargetMinutes(roster);
  return roster.slice(0, size);
}
